import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/2 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src="/logo/Load Surf SVG.svg" 
              alt="LoadSurf AI" 
              className="h-12 w-12"
            />
            <span className="text-2xl font-bold text-white">
              LoadSurf<span className="text-amber-400">.AI</span>
            </span>
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Terms & Conditions
          </h1>
          <div className="flex items-center justify-center gap-4 text-slate-400">
            <p>Effective Date: January 13, 2026</p>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <p>Last Updated: January 13, 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-3xl p-8 sm:p-12">
          <div className="prose prose-invert prose-slate max-w-none">
            {/* Intro */}
            <p className="text-slate-300 leading-relaxed mb-8">
              These Terms & Conditions ("Terms") govern your access to and use of the website located at{' '}
              <a href="https://www.LoadSurf.ai" className="text-sky-400 hover:text-sky-300">www.LoadSurf.ai</a>{' '}
              (the "Site"), as well as any software applications, tools, APIs, integrations, features, or other products and services offered now or in the future by LoadSurf AI, Inc. ("LoadSurf AI," "we," "us," or "our") (collectively, the "Services").
            </p>
            <p className="text-slate-300 leading-relaxed mb-12">
              By accessing or using the Services, creating an account, or connecting third-party services, you agree to be bound by these Terms. If you do not agree, you may not access or use the Services.
            </p>

            {/* Section 1 */}
            <Section number="1" title="Eligibility & Authority">
              <p>You represent and warrant that you:</p>
              <ul>
                <li>Are at least 18 years old</li>
                <li>Have the legal authority to bind yourself or the business you represent</li>
                <li>Will use the Services only for lawful business purposes</li>
              </ul>
              <p>
                If you are using the Services on behalf of an entity, you represent that you are authorized to bind that entity to these Terms.
              </p>
            </Section>

            {/* Section 2 */}
            <Section number="2" title="Account Registration & Security">
              <p>You are responsible for:</p>
              <ul>
                <li>Maintaining accurate account information</li>
                <li>Safeguarding your login credentials</li>
                <li>All activity occurring under your account</li>
              </ul>
              <p>
                You must notify LoadSurf AI immediately of any unauthorized access or security breach.
              </p>
            </Section>

            {/* Section 3 */}
            <Section number="3" title="Description of the Services">
              <p>
                LoadSurf AI provides technology that aggregates access to private load boards and other third-party platforms and enables users to connect those accounts with dispatch systems, automation tools, and related services, including integrations with third-party products such as Hey Bubba! and others.
              </p>
              <p>
                The Services may change, evolve, or be discontinued at any time without notice.
              </p>
            </Section>

            {/* Section 4 */}
            <Section number="4" title="License & Restrictions">
              <p>
                Subject to your compliance with these Terms, LoadSurf AI grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services.
              </p>
              <p>You may not:</p>
              <ul>
                <li>Reverse engineer, decompile, or disassemble the Services</li>
                <li>Resell, sublicense, or make the Services available to unauthorized parties</li>
                <li>Circumvent security or technical safeguards</li>
                <li>Use the Services in violation of applicable laws or third-party agreements</li>
              </ul>
            </Section>

            {/* Section 5 */}
            <Section number="5" title="Third-Party Services & Integrations">
              <p>
                The Services rely on and integrate with third-party platforms ("Third-Party Services").
              </p>
              <p>You acknowledge that:</p>
              <ul>
                <li>LoadSurf AI does not control Third-Party Services</li>
                <li>Third-Party Services may change, suspend, or revoke access at any time</li>
                <li>Your use of Third-Party Services is governed solely by their own terms</li>
                <li>LoadSurf AI is not responsible for third-party outages, errors, pricing, or data accuracy</li>
              </ul>
            </Section>

            {/* Section 6 */}
            <Section number="6" title="Credentials & Authorization">
              <p>By connecting Third-Party Services, you represent that:</p>
              <ul>
                <li>You are authorized to access those accounts</li>
                <li>You grant LoadSurf AI permission to access, process, and transmit data solely to provide the Services</li>
              </ul>
              <p>
                You remain responsible for compliance with all third-party terms.
              </p>
            </Section>

            {/* Section 7 */}
            <Section number="7" title="Fees, Trials & Billing">
              <p>
                Certain Services may require payment.
              </p>
              <p>
                By providing payment information, you authorize LoadSurf AI to charge applicable fees. Failure to pay may result in suspension or termination. Fees are non-refundable unless expressly stated otherwise.
              </p>
            </Section>

            {/* Section 8 */}
            <Section number="8" title="Acceptable Use">
              <p>You agree not to:</p>
              <ul>
                <li>Violate laws or regulations</li>
                <li>Interfere with system integrity or security</li>
                <li>Misuse data or attempt unauthorized access</li>
                <li>Misrepresent authorization or identity</li>
              </ul>
              <p>
                Violations may result in immediate suspension or termination.
              </p>
            </Section>

            {/* Section 9 */}
            <Section number="9" title="Intellectual Property">
              <p>
                All intellectual property rights in the Services are owned by or licensed to LoadSurf AI.
              </p>
              <p>
                You retain ownership of your data. You grant LoadSurf AI a worldwide, royalty-free license to use, process, and display such data solely to operate and improve the Services.
              </p>
            </Section>

            {/* Section 10 */}
            <Section number="10" title="Confidentiality">
              <p>
                Each party agrees to protect confidential, non-public information obtained through the Services and to use such information only as necessary to perform under these Terms.
              </p>
            </Section>

            {/* Section 11 */}
            <Section number="11" title="Disclaimer of Warranties">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 my-4">
                <p className="text-slate-300 font-medium uppercase text-sm">
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE."
                </p>
                <p className="text-slate-300 font-medium uppercase text-sm mt-2">
                  LOADSURF AI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>
            </Section>

            {/* Section 12 */}
            <Section number="12" title="Limitation of Liability">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 my-4">
                <p className="text-slate-300 font-medium uppercase text-sm">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOADSURF AI SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES.
                </p>
                <p className="text-slate-300 font-medium uppercase text-sm mt-2">
                  LOADSURF AI'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO LOADSURF AI IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100 IF NO FEES WERE PAID.
                </p>
              </div>
            </Section>

            {/* Section 13 */}
            <Section number="13" title="Indemnification">
              <p>
                You agree to indemnify and hold harmless LoadSurf AI from any claims, losses, or expenses arising from:
              </p>
              <ul>
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your misuse of Third-Party Services</li>
              </ul>
            </Section>

            {/* Section 14 */}
            <Section number="14" title="Suspension & Termination">
              <p>
                LoadSurf AI may suspend or terminate your access at any time for violations of these Terms, non-payment, or risk to the Services.
              </p>
            </Section>

            {/* Section 15 */}
            <Section number="15" title="Governing Law">
              <p>
                These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles.
              </p>
            </Section>

            {/* Section 16 */}
            <Section number="16" title="Dispute Resolution; Arbitration; Class Action Waiver">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                <p className="text-amber-400 font-semibold text-sm">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.
                </p>
              </div>

              <h4 className="text-white font-semibold mt-6 mb-2">16.1 Informal Resolution</h4>
              <p>
                Before initiating any formal dispute, you agree to contact LoadSurf AI at{' '}
                <a href="mailto:info@loadsurf.ai" className="text-sky-400 hover:text-sky-300">info@loadsurf.ai</a>{' '}
                and attempt to resolve the dispute informally within thirty (30) days.
              </p>

              <h4 className="text-white font-semibold mt-6 mb-2">16.2 Mandatory Mediation</h4>
              <p>
                If informal resolution fails, the dispute shall be submitted to non-binding mediation, conducted remotely or in Delaware, with costs shared equally unless otherwise agreed.
              </p>

              <h4 className="text-white font-semibold mt-6 mb-2">16.3 Binding Arbitration</h4>
              <p>
                If mediation does not resolve the dispute, the dispute shall be resolved by binding arbitration on an individual basis, administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.
              </p>
              <ul>
                <li>Arbitration shall occur in Delaware or remotely, at LoadSurf AI's election.</li>
                <li>The arbitrator has exclusive authority to resolve disputes relating to these Terms.</li>
                <li>The arbitrator may award relief only on an individual basis.</li>
              </ul>

              <h4 className="text-white font-semibold mt-6 mb-2">16.4 Class Action Waiver</h4>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 my-4">
                <p className="text-slate-300 font-medium uppercase text-sm">
                  YOU AND LOADSURF AI AGREE THAT ALL CLAIMS MUST BE BROUGHT IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS, COLLECTIVE, OR REPRESENTATIVE ACTION.
                </p>
                <p className="text-slate-300 font-medium uppercase text-sm mt-2">
                  CLASS ACTIONS AND CLASS ARBITRATIONS ARE NOT PERMITTED.
                </p>
              </div>

              <h4 className="text-white font-semibold mt-6 mb-2">16.5 Exceptions</h4>
              <p>Either party may seek injunctive or equitable relief in court for:</p>
              <ul>
                <li>Intellectual property infringement</li>
                <li>Unauthorized access or misuse of the Services</li>
              </ul>

              <h4 className="text-white font-semibold mt-6 mb-2">16.6 Opt-Out</h4>
              <p>
                You may opt out of arbitration by sending written notice to{' '}
                <a href="mailto:info@loadsurf.ai" className="text-sky-400 hover:text-sky-300">info@loadsurf.ai</a>{' '}
                within thirty (30) days of first accepting these Terms.
              </p>
            </Section>

            {/* Section 17 */}
            <Section number="17" title="Assignment">
              <p>
                LoadSurf AI may assign or transfer these Terms in connection with a merger, acquisition, restructuring, or sale of assets without notice.
              </p>
            </Section>

            {/* Section 18 */}
            <Section number="18" title="Changes to These Terms">
              <p>
                We may update these Terms from time to time. Continued use of the Services constitutes acceptance of the updated Terms.
              </p>
            </Section>

            {/* Section 19 */}
            <Section number="19" title="Contact">
              <p>
                For questions about these Terms, please contact us at:{' '}
                <a href="mailto:info@loadsurf.ai" className="text-sky-400 hover:text-sky-300">info@loadsurf.ai</a>
              </p>
            </Section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-12">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <span className="mx-4 text-slate-700">•</span>
          <Link 
            to="/privacy"
            className="text-slate-400 hover:text-white transition-colors"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

// Section component for consistent styling
const Section = ({ number, title, children }) => {
  return (
    <section className="mb-10">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-3">
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-sky-500/10 text-sky-400 text-sm font-bold">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-slate-300 leading-relaxed space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ul]:text-slate-400">
        {children}
      </div>
    </section>
  );
};

export default TermsPage;
